import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Button, Menu, MenuItem, Stack } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { memo, type ReactNode, useCallback, useId, useMemo, useState } from 'react';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { SelectMenu, type SelectMenuOption } from 'src/components/SelectMenu';
import type { WorkflowListItem } from 'src/graphql/entities/workflow';
import { ActiveStatusDot } from 'src/pages/Settings/workflows/workflows-page.elements';
import type { WorkflowVersionRaw } from 'src/types';
import {
  getWorkflowVersionLabel,
  getWorkflowVersionOptionPrimaryLabel,
  getWorkflowVersionOptionSecondaryLabel,
} from './workflow-version-option-label';

dayjs.extend(relativeTime);

type WorkflowItem = Pick<WorkflowListItem, 'workflowId' | 'name' | 'isPlatform'>;

type WorkflowVersionItem = Pick<
  WorkflowVersionRaw,
  'workflowVersionId' | 'version' | 'isActive' | 'updatedAt' | 'description'
>;

interface WorkflowSelectionToolbarProps {
  workflows: WorkflowItem[];
  workflowVersions: WorkflowVersionItem[];
  selectedWorkflowId?: string;
  selectedVersionId?: string;
  workflowSelectDisabled: boolean;
  versionSelectDisabled: boolean;
  newDraftVersionDisabled: boolean;
  onWorkflowChange: (workflowId?: string) => void;
  onVersionChange: (workflowVersionId?: string) => void;
  onNewWorkflow: () => void;
  onNewDraftVersion: () => void;
  onOpenAssignments: () => void;
  versionActions?: ReactNode;
}

const dropdownWidthStyles = {
  width: { xs: '100%', sm: 220, md: 260 },
  maxWidth: 320,
};

const getWorkflowGroup = (workflow: WorkflowItem) =>
  workflow.isPlatform ? 'Platform workflows' : 'Your workflows';

const WorkflowSelectionToolbarComponent = ({
  workflows,
  workflowVersions,
  selectedWorkflowId,
  selectedVersionId,
  workflowSelectDisabled,
  versionSelectDisabled,
  newDraftVersionDisabled,
  onWorkflowChange,
  onVersionChange,
  onNewWorkflow,
  onNewDraftVersion,
  onOpenAssignments,
  versionActions,
}: WorkflowSelectionToolbarProps) => {
  const workflowTriggerId = useId();
  const versionTriggerId = useId();
  const [newMenuAnchor, setNewMenuAnchor] = useState<HTMLElement | null>(null);
  const closeNewMenu = useCallback(() => setNewMenuAnchor(null), []);
  const getRelativeTime = useCallback(
    (value?: string | null) => (value ? dayjs(value).fromNow() : '—'),
    [],
  );

  const workflowOptions = useMemo<WorkflowItem[]>(
    () =>
      workflows.map((workflow) => ({
        ...workflow,
        workflowId: String(workflow.workflowId),
      })),
    [workflows],
  );

  const versionOptions = useMemo<WorkflowVersionItem[]>(
    () =>
      workflowVersions.map((workflowVersion) => ({
        ...workflowVersion,
        workflowVersionId: String(workflowVersion.workflowVersionId),
      })),
    [workflowVersions],
  );

  const hasPlatformWorkflows = workflowOptions.some((workflow) => workflow.isPlatform);

  const workflowValue = useMemo<WorkflowItem | null>(
    () =>
      workflowOptions.find((workflow) => workflow.workflowId === selectedWorkflowId) ?? null,
    [selectedWorkflowId, workflowOptions],
  );

  const versionValue = useMemo<WorkflowVersionItem | null>(
    () =>
      versionOptions.find((workflowVersion) => {
        return workflowVersion.workflowVersionId === selectedVersionId;
      }) ?? null,
    [selectedVersionId, versionOptions],
  );

  const workflowMenuOptions = useMemo<SelectMenuOption[]>(
    () =>
      workflowOptions.map((workflow) => ({
        value: workflow.workflowId,
        title: workflow.name ?? '',
        group: hasPlatformWorkflows ? getWorkflowGroup(workflow) : undefined,
      })),
    [hasPlatformWorkflows, workflowOptions],
  );

  const versionMenuOptions = useMemo<SelectMenuOption[]>(
    () =>
      versionOptions.map((workflowVersion) => ({
        value: workflowVersion.workflowVersionId,
        title: getWorkflowVersionOptionPrimaryLabel(workflowVersion.description),
        description: getWorkflowVersionOptionSecondaryLabel(
          workflowVersion.version,
          getRelativeTime(workflowVersion.updatedAt),
        ),
        keywords: [getWorkflowVersionLabel(workflowVersion.version)],
        // Every row gets a dot, hidden when inactive, so the titles share one column.
        icon: workflowVersion.isActive ? (
          <ActiveStatusDot aria-label="Active version" />
        ) : (
          <ActiveStatusDot aria-hidden sx={{ visibility: 'hidden' }} />
        ),
      })),
    [getRelativeTime, versionOptions],
  );

  return (
    <Stack
      spacing={2}
      sx={{
        width: '100%',
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        sx={{
          width: '100%',
          alignItems: { xs: 'stretch', lg: 'flex-end' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'flex-end' }}
          flexWrap="wrap"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Stack sx={dropdownWidthStyles}>
            <FormFieldLabel htmlFor={workflowTriggerId}>Workflow</FormFieldLabel>
            <SelectMenu
              id={workflowTriggerId}
              label="Workflow"
              variant="field"
              menuWidth="trigger"
              placeholder="Select Workflow"
              options={workflowMenuOptions}
              sectioned
              value={workflowValue?.workflowId ?? null}
              disabled={workflowSelectDisabled}
              emptyText={workflowSelectDisabled ? 'No workflows' : 'No matches'}
              clearable
              onClear={() => onWorkflowChange(undefined)}
              onChange={(workflowId) => onWorkflowChange(workflowId)}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ minWidth: 0 }}>
            <Stack sx={dropdownWidthStyles}>
              <FormFieldLabel htmlFor={versionTriggerId}>Version</FormFieldLabel>
              <SelectMenu
                id={versionTriggerId}
                label="Version"
                variant="field"
                menuWidth="trigger"
                placeholder="Select Version"
                options={versionMenuOptions}
                plainOptionIcons
                value={versionValue?.workflowVersionId ?? null}
                triggerLabel={
                  versionValue ? getWorkflowVersionLabel(versionValue.version) : undefined
                }
                // `false`, not undefined: undefined falls back to the picked row's icon, and an
                // inactive version's hidden spacer dot would leave a gap on the trigger. The
                // wrapper takes the icon slot's stretch so the dot keeps its own size.
                triggerIcon={
                  versionValue?.isActive ? (
                    <Stack component="span" alignItems="center" justifyContent="center">
                      <ActiveStatusDot aria-hidden />
                    </Stack>
                  ) : (
                    false
                  )
                }
                disabled={versionSelectDisabled}
                emptyText={versionSelectDisabled ? 'Select workflow first' : 'No matches'}
                clearable
                onClear={() => onVersionChange(undefined)}
                onChange={(workflowVersionId) => onVersionChange(workflowVersionId)}
              />
            </Stack>

            {versionActions}
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1}
          sx={{ alignSelf: { xs: 'stretch', lg: 'flex-end' } }}
        >
          {/* Assignments are per partner and per trigger, not per workflow, so this sits
              outside the workflow/version selects and stays enabled with nothing selected. */}
          <Button
            variant="outlined"
            onClick={onOpenAssignments}
            data-testid="workflows-assignments-open"
          >
            Assignments
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            aria-haspopup="menu"
            aria-expanded={newMenuAnchor ? 'true' : undefined}
            onClick={(event) => setNewMenuAnchor(event.currentTarget)}
            data-testid="workflows-new"
          >
            New
          </Button>
          <Menu anchorEl={newMenuAnchor} open={!!newMenuAnchor} onClose={closeNewMenu}>
            <MenuItem
              onClick={() => {
                closeNewMenu();
                onNewWorkflow();
              }}
              data-testid="workflows-new-workflow"
            >
              Workflow
            </MenuItem>
            {/* The id the old Create Draft Version button carried: it opens the same dialog. */}
            <MenuItem
              disabled={newDraftVersionDisabled}
              onClick={() => {
                closeNewMenu();
                onNewDraftVersion();
              }}
              data-testid="workflows-create-draft"
            >
              Draft version
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>
    </Stack>
  );
};

WorkflowSelectionToolbarComponent.displayName = 'WorkflowSelectionToolbar';

export const WorkflowSelectionToolbar = memo(WorkflowSelectionToolbarComponent);
